import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleAlert, X } from 'lucide-react';
import { questionById, SetupQuestion } from '../../../config/setupQuestions';
import { ScheduleEntry, TimeRule } from '../../../types';
import { getAnswer, LineDraft, setNone, setOwn, setSame, startingLines } from '../../../utils/setupAnswers';
import { canBeWeekly, canLimit, draftToTimeRule, timeRuleToDraft } from '../../../utils/timeRuleDraft';
import TimeRuleFields from '../TimeRuleFields';
import { CANDLE_LIGHTING_ZMAN } from '../../../utils/timeUtils';

const DEFAULT_TIME: Partial<Record<string, string>> = {
  selichot: '06:00', shacharit: '07:00', mincha: '13:30', maariv: '20:00', kabalatShabbat: '18:00',
};

const inputClass =
  'w-full px-3 py-2 rounded-[10px] border border-ink/20 bg-paper text-ink placeholder:text-ash focus:outline-none focus:ring-2 focus:ring-ink/30 focus:border-ink text-sm';

/** Short display of a time rule, e.g. "07:00" or "Sunset −15". */
export function useFormatRule() {
  const { t } = useTranslation();
  return (rule: TimeRule): string => {
    if (rule.type === 'fixed') return rule.time;
    const key = rule.baseZman.charAt(0).toLowerCase() + rule.baseZman.slice(1);
    const name = t(`zmanim.${key}`);
    if (!rule.offsetMinutes) return name;
    return `${name} ${rule.offsetMinutes > 0 ? '+' : '−'}${Math.abs(rule.offsetMinutes)}`;
  };
}

export function useQuestionTitle() {
  const { t } = useTranslation();
  return (q: SetupQuestion) => t(`setup.q.${q.id}.title`);
}

/** The "!" button: opens the full explanation of a question. */
export function ExplainButton({ q, open, onToggle }: { q: SetupQuestion; open: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={`help-${q.id}`}
      aria-label={t('setup.ui.explain')}
      className={`shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full border ${
        open ? 'bg-ink text-paper border-ink' : 'border-ink/20 text-ink hover:bg-fog'
      }`}
    >
      {open ? <X className="w-4 h-4" /> : <CircleAlert className="w-4 h-4" />}
    </button>
  );
}

interface QuestionEditorProps {
  q: SetupQuestion;
  /** The shul's entries as being edited (a draft); changes come back through onChange. */
  schedules: ScheduleEntry[];
  onChange: (next: ScheduleEntry[]) => void;
}

/**
 * One set-up question: "Same as …" / "Different time" (or "Set the time") /
 * "No minyan", and the minyan lines when the shul has its own times.
 */
export default function QuestionEditor({ q, schedules, onChange }: QuestionEditorProps) {
  const { t } = useTranslation();
  const formatRule = useFormatRule();
  const titleOf = useQuestionTitle();
  const [helpOpen, setHelpOpen] = useState(false);

  const answer = getAnswer(schedules, q);
  const linked = q.sameAs ? questionById(q.sameAs) : undefined;
  const linkedAnswer = linked ? getAnswer(schedules, linked) : undefined;
  const isOwn = answer.mode === 'own';
  const lines: LineDraft[] = isOwn ? startingLines(schedules, q) : [];

  const chooseSame = () => onChange(setSame(schedules, q));
  const chooseNone = () => onChange(setNone(schedules, q));
  const chooseOwn = () => {
    if (isOwn) return;
    const start = startingLines(schedules, q);
    const fallbackRule: TimeRule = { type: 'fixed', time: DEFAULT_TIME[q.prayer] ?? '08:00' };
    onChange(setOwn(schedules, q, start.length ? start : [{ timeRule: fallbackRule }]));
  };
  const saveLines = (next: LineDraft[]) => onChange(setOwn(schedules, q, next));

  // Linked "same" for a required question needs the other question answered first.
  const sameDisabled = !!linked && q.kind === 'required' && (!linkedAnswer || linkedAnswer.entries.length === 0);
  const sameSummary = linkedAnswer
    ? linkedAnswer.entries.length
      ? linkedAnswer.entries.map((e) => formatRule(e.timeRule)).join(' · ')
      : linkedAnswer.mode === 'unset' ? t('setup.ui.linkedNotSet') : t('setup.ui.noMinyanThere')
    : '';

  const choice = (mode: string, label: string, sub: string | null, checked: boolean, onSelect: () => void, disabled = false) => {
    const id = `q-${q.id}-${mode}`;
    return (
      <label
        htmlFor={id}
        className={`flex items-start gap-2 rounded-[10px] border px-3 py-2 text-sm cursor-pointer ${
          checked ? 'border-weekday-600 bg-weekday-50' : 'border-ink/10'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <input id={id} type="radio" name={`q-${q.id}`} checked={checked} disabled={disabled} onChange={onSelect} className="mt-1" />
        <span className="min-w-0">
          <span className="text-ink">{label}</span>
          {sub && <span className="block text-xs text-ash break-words">{sub}</span>}
        </span>
      </label>
    );
  };

  const unset = answer.mode === 'unset';

  return (
    <div className={`rounded-[16px] border bg-paper p-3 space-y-3 ${unset ? 'border-gold-600' : 'border-ink/10'}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-ink">{titleOf(q)}</h3>
          {unset && <p className="text-xs font-medium text-gold-700">{t('setup.ui.notAnsweredYet')}</p>}
        </div>
        <ExplainButton q={q} open={helpOpen} onToggle={() => setHelpOpen((o) => !o)} />
      </div>
      {helpOpen && (
        <p id={`help-${q.id}`} className="text-sm text-ink bg-fog rounded-[10px] p-3 leading-relaxed">
          {t(`setup.q.${q.id}.help`)}
        </p>
      )}

      <div className="space-y-2" role="radiogroup" aria-label={titleOf(q)}>
        {linked &&
          choice('same', t('setup.ui.sameAs', { what: titleOf(linked) }), sameSummary, answer.mode === 'same', chooseSame, sameDisabled)}
        {choice('own', linked ? t('setup.ui.different') : t('setup.ui.setTime'), null, isOwn, chooseOwn)}
        {choice('none', t('setup.ui.noMinyan'), null, answer.mode === 'none', chooseNone)}
      </div>

      {isOwn && (
        <div className="space-y-3">
          {lines.map((line, i) => {
            const entry = schedules.find((e) => e.id === line.id);
            const otherDays = entry ? entry.dayTypes.filter((d) => d !== q.dayType) : [];
            const update = (patch: Partial<LineDraft>) => saveLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
            const draft = timeRuleToDraft(line.timeRule);
            return (
              <div key={line.id ?? i} className="rounded-[10px] border border-ink/10 p-3 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-ash">
                    {t('setup.ui.minyanN', { n: i + 1 })}
                    {entry?.season && ` · ${t(entry.season === 'summerClock' ? 'admin.seasonSummer' : 'admin.seasonWinter')}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => saveLines(lines.filter((_, j) => j !== i))}
                    className="text-xs text-danger-600 hover:underline"
                  >
                    {t('setup.ui.remove')}
                  </button>
                </div>
                <TimeRuleFields
                  value={draft}
                  onChange={(patch) => update({ timeRule: draftToTimeRule({ ...draft, ...patch }) })}
                  idPrefix={`q-${q.id}-${i}`}
                  radioName={`q-${q.id}-${i}-type`}
                  extraZmanim={[{ key: CANDLE_LIGHTING_ZMAN, label: t('zmanim.candleLighting') }]}
                  allowLimit={canLimit(q.prayer, [q.dayType])}
                  allowWeekly={canBeWeekly(q.prayer, [q.dayType])}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input
                    id={`q-${q.id}-${i}-note`}
                    aria-label={t('setup.ui.noteEn')}
                    placeholder={t('setup.ui.noteEn')}
                    value={line.note ?? ''}
                    onChange={(e) => update({ note: e.target.value })}
                    className={inputClass}
                  />
                  <input
                    id={`q-${q.id}-${i}-noteHe`}
                    aria-label={t('setup.ui.noteHe')}
                    placeholder={t('setup.ui.noteHe')}
                    value={line.noteHe ?? ''}
                    onChange={(e) => update({ noteHe: e.target.value })}
                    className={inputClass}
                    dir="rtl"
                  />
                </div>
                {otherDays.length > 0 && (
                  <p className="text-xs text-ash">
                    {t('setup.ui.alsoUsedFor', { days: otherDays.map((d) => t(`dayTypes.${d}`)).join(', ') })}
                  </p>
                )}
              </div>
            );
          })}
          <button
            type="button"
            onClick={() => saveLines([...lines, { timeRule: { type: 'fixed', time: DEFAULT_TIME[q.prayer] ?? '08:00' } }])}
            className="text-sm font-medium text-weekday-700 hover:underline"
          >
            {t('setup.ui.addMinyan')}
          </button>
        </div>
      )}
    </div>
  );
}
